<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { debounce } from '@chatwoot/utils';

import Popover from 'dashboard/components-next/popover/Popover.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import SearchAPI from 'dashboard/api/search';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';

const props = defineProps({
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const isSearching = ref(false);
const isSubmitting = ref(false);
const results = ref([]);
const query = ref('');
const selectedTargetId = ref(null);

const v$ = useVuelidate(
  { selectedTargetId: { required } },
  { selectedTargetId }
);

const currentInboxId = computed(() => props.currentChat?.inbox_id);

const conversationLabel = (id, name, inbox) =>
  `#${id}${name ? ` · ${name}` : ''}${inbox ? ` · ${inbox}` : ''}`;

// A message carries the inbox it was exchanged through, so only conversations of the same inbox
// can take the ones being moved.
const targetConversationList = computed(() =>
  results.value
    .filter(
      item =>
        String(item.id) !== String(props.currentChat?.id) &&
        item.inbox?.id === currentInboxId.value
    )
    .map(item => ({
      value: item.id,
      label: conversationLabel(item.id, item.contact?.name, item.inbox?.name),
    }))
);

const selectedTargetConversation = computed(() =>
  results.value.find(item => String(item.id) === String(selectedTargetId.value))
);

const sourceContact = computed(
  () => props.currentChat?.contact ?? props.currentChat?.meta?.sender ?? {}
);

const sourceLabel = computed(() =>
  conversationLabel(
    props.currentChat?.id ?? '',
    sourceContact.value.name,
    props.currentChat?.inbox?.name ?? props.currentChat?.inbox_name
  )
);

const targetLabel = computed(() => {
  const target = selectedTargetConversation.value;
  if (!target) return '';

  return conversationLabel(target.id, target.contact?.name, target.inbox?.name);
});

const hasValidationError = computed(() => v$.value.selectedTargetId.$error);
const validationErrorMessage = computed(() =>
  hasValidationError.value ? t('MERGE_MESSAGES.TARGET_REQUIRED_ERROR') : ''
);

const fetchConversations = async () => {
  if (query.value.trim().length < MIN_SEARCH_LENGTH) {
    results.value = [];
    return;
  }

  isSearching.value = true;
  results.value = [];

  try {
    const { data } = await SearchAPI.conversations({ q: query.value });
    results.value = data?.payload?.conversations ?? [];
  } catch (error) {
    useAlert(t('MERGE_MESSAGES.SEARCH_ERROR'));
  } finally {
    isSearching.value = false;
  }
};

const debouncedSearch = debounce(fetchConversations, SEARCH_DEBOUNCE_MS, false);

const onSearch = searchQuery => {
  query.value = searchQuery;
  debouncedSearch();
};

const reset = () => {
  query.value = '';
  results.value = [];
  selectedTargetId.value = null;
  v$.value.$reset();
};

const onCancel = hide => {
  hide();
  reset();
  emit('close');
};

const onSubmit = async hide => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  isSubmitting.value = true;

  try {
    await ConversationAPI.mergeMessages({
      sourceId: props.currentChat.id,
      targetId: selectedTargetId.value,
    });
    useAlert(t('MERGE_MESSAGES.SUCCESS'));
    router.push({
      path: frontendURL(
        conversationUrl({
          accountId: route.params.accountId,
          id: selectedTargetId.value,
        })
      ),
    });
    onCancel(hide);
  } catch (error) {
    useAlert(t('MERGE_MESSAGES.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Popover @hide="$emit('close')">
    <slot name="trigger" />
    <template #content="{ hide }">
      <div class="w-full md:w-[28rem] p-6 flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <h3 class="text-base font-medium leading-6 text-n-slate-12">
            {{ t('MERGE_MESSAGES.TITLE') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('MERGE_MESSAGES.DESC') }}
          </p>
        </div>

        <form class="flex flex-col gap-4" @submit.prevent="onSubmit(hide)">
          <div class="flex flex-col gap-2">
            <div class="flex gap-2 justify-between items-center h-5">
              <label class="text-sm text-n-slate-12">
                {{ t('MERGE_MESSAGES.TARGET_LABEL') }}
              </label>
              <span
                class="flex justify-center items-center w-24 h-5 text-xs rounded-md text-n-teal-11 bg-n-alpha-2"
              >
                {{ t('MERGE_MESSAGES.TARGET_HELP_LABEL') }}
              </span>
            </div>
            <ComboBox
              id="merge-messages-target"
              use-api-results
              :model-value="selectedTargetId"
              :options="targetConversationList"
              :empty-state="
                isSearching
                  ? t('MERGE_MESSAGES.IS_SEARCHING')
                  : t('MERGE_MESSAGES.NO_RESULTS')
              "
              :search-placeholder="t('MERGE_MESSAGES.SEARCH_PLACEHOLDER')"
              :placeholder="t('MERGE_MESSAGES.PLACEHOLDER')"
              :has-error="hasValidationError"
              :message="validationErrorMessage"
              class="[&>div>button]:bg-n-alpha-black2"
              @update:model-value="value => (selectedTargetId = value)"
              @search="onSearch"
            />
          </div>

          <div class="flex relative gap-2 justify-center top-1">
            <div v-for="i in 3" :key="i" class="relative w-4 h-8">
              <div
                class="absolute w-0 h-0 border-l-[4px] border-r-[4px] border-b-[6px] border-l-transparent border-r-transparent border-n-strong ltr:translate-x-[4px] rtl:-translate-x-[4px] -translate-y-[4px]"
              />
              <div
                class="absolute w-[1px] h-full bg-n-strong left-1/2 transform -translate-x-1/2"
              />
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex gap-2 justify-between items-center h-5">
              <label class="text-sm text-n-slate-12">
                {{ t('MERGE_MESSAGES.SOURCE_LABEL') }}
              </label>
              <span
                class="flex justify-center items-center w-24 h-5 text-xs rounded-md text-n-ruby-11 bg-n-alpha-2"
              >
                {{ t('MERGE_MESSAGES.SOURCE_HELP_LABEL') }}
              </span>
            </div>
            <div
              class="flex gap-2 items-center p-3 rounded-xl border border-n-strong h-[60px]"
            >
              <Avatar
                :name="sourceContact.name || ''"
                :src="sourceContact.thumbnail || ''"
                :size="32"
                rounded-full
              />
              <div class="flex flex-col gap-1 w-full min-w-0">
                <span class="text-sm leading-4 truncate text-n-slate-11">
                  {{ sourceLabel }}
                </span>
                <span class="text-sm leading-4 truncate text-n-slate-11">
                  {{ sourceContact.email || sourceContact.phone_number || '' }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="selectedTargetId"
            class="my-2 relative p-2.5 border rounded-[4px] text-n-slate-12 border-n-weak bg-n-background"
          >
            <h5 class="text-base font-medium text-n-slate-12">
              {{ t('MERGE_MESSAGES.SUMMARY_TITLE') }}
            </h5>
            <ul class="ml-0 list-none">
              <li>
                {{
                  t('MERGE_MESSAGES.SUMMARY_SOURCE', {
                    conversation: sourceLabel,
                  })
                }}
              </li>
              <li>
                {{
                  t('MERGE_MESSAGES.SUMMARY_TARGET', {
                    conversation: targetLabel,
                  })
                }}
              </li>
              <li class="mt-2 text-xs text-n-slate-11">
                {{ t('MERGE_MESSAGES.SUMMARY_NOTE') }}
              </li>
            </ul>
          </div>

          <div class="flex gap-2 justify-end mt-2">
            <Button
              slate
              faded
              type="button"
              :label="t('MERGE_MESSAGES.CANCEL')"
              @click="onCancel(hide)"
            />
            <Button
              type="submit"
              ruby
              :is-loading="isSubmitting"
              :label="t('MERGE_MESSAGES.SUBMIT')"
            />
          </div>
        </form>
      </div>
    </template>
  </Popover>
</template>
